import S, { DataSignal } from 's-js';
import { AssertExtends } from 'lib/assertExtends';
import { notNullish } from 'lib/notNullish';
import UOM from 'lib/UOM';

import { AddPortCallRequest } from 'data/models/AddPortCallRequest';
import { PCRoles } from 'data/models/PCRoles';
import { PortCall } from 'data/models/PortCall';

import { PCCache } from 'data/PCCache';

import { LocalVesselSearch, LocalVesselSearchParams } from 'portcall/localvesselsearch/model';
import { NewVessel, NewVesselParams } from 'portcall/newvessel/model';
import { ToggleableMeasurement } from 'portcall/toggleablemeasurement/model';
import { UserSelector } from 'portcall/userselector/model';
import { UserSummary } from 'data/models/UserSummary';

import { AddPortCallTabModel } from './model';

export interface AddPortCallVesselTabParams {
    PortCall : PortCall;
    Request : AddPortCallRequest;
}

export enum VesselType {
    Existing = 'existing',
    Import = 'import',
    LengthOnly = 'length-only'
}

const lengthConversions = [{
    uom: UOM.Foot,
    // TH: To display as feet and inches instead of as decimal feet, uncomment the feetAndInches property and comment out the decimalPlaces property.
    // That requires CSS tweaks to make room for the wider field, so in the interest of getting something in, I'm holding off for now.
    //feetAndInches: true
    decimalPlaces: 2
}, {
    uom: UOM.Meter,
    decimalPlaces: 1
}];

export type AddPortCallVesselTab = AssertExtends<ReturnType<typeof AddPortCallVesselTab>, AddPortCallTabModel>;
export function AddPortCallVesselTab(
    user : () => UserSummary | null,
    data : PCCache,
    localVesselSearchModel : (params : LocalVesselSearchParams) => LocalVesselSearch,
    newVesselModel : (params : NewVesselParams) => NewVessel,
    { PortCall, Request } : AddPortCallVesselTabParams
) {
    const
        vesselType = S.value(VesselType.Existing),
        LocalSearch = localVesselSearchModel({ onSelect: v => PortCall.VesselId(v.Id()) }),
        NewVessel = S(() =>
            vesselType() === VesselType.Import ?
                newVesselModel({
                    lengthUOM: () => PortCall.LengthOnlyLength.UOM() ?? UOM.Foot,
                    depthUOM: () => PortCall.DeepDraft.UOM() ?? UOM.Foot,
                    onSelect: v => PortCall.VesselId(v.Id()),
                    onDismiss: () => vesselType(VesselType.Existing)
                }) :
                null
        ),
        LengthOnlyLengthUOM = S.value(UOM.Foot),
        LengthOnlyLengthInFeet : DataSignal<boolean> = (val? : boolean) => {
            if (val === undefined) {
                return LengthOnlyLengthUOM() === UOM.Foot;
            } else {
                LengthOnlyLengthUOM(val ? UOM.Foot : UOM.Meter);
                return val;
            }
        },
        ToggleableLengthOnlyLength = ToggleableMeasurement({
            backingMeasurement: PortCall.LengthOnlyLength,
            uom: LengthOnlyLengthUOM,
            conversions: lengthConversions
        }),
        availableAgents = S(() => {
            const r = data.Roles.index(PCRoles.ReserveBerths)();
            return r?.Users()
                .map(ur => ur.User()?.PCUser())
                .filter(notNullish)
                .filter(u => u.Active()) ?? [];
        }),
        AgentSelector = UserSelector({ UserId: PortCall.AgentId, Users: availableAgents }),
        vesselReqSatisfied = () =>
            (NewVessel()?.CanSave() ?? false)
         || vesselType() === VesselType.Existing && !!PortCall.Vessel()
         || vesselType() === VesselType.LengthOnly && !!PortCall.LengthOnlyLength.Num(),
        CanProceed = () => vesselReqSatisfied() && !!PortCall.Agent();

    S(() => {
        if (vesselType() !== VesselType.Existing) PortCall.VesselId(null);
    });

    S(() => {
        if (vesselType() !== VesselType.LengthOnly) PortCall.LengthOnlyLength.Num(null);
    });

    S(() => {
        if (NewVessel()) Request.AddVessel(NewVessel()!.CreateRequest());
        else Request.AddVessel(null);
    });

    S(() => AgentSelector.data(PortCall.Agent()?.FullName() ?? ''));

    return {
        User: user,
        PortCall,
        VesselType: vesselType,
        LocalSearch,
        NewVessel,
        LengthOnlyLengthInFeet,
        ToggleableLengthOnlyLength,
        AgentSelector,
        CanProceed
    };
}



// WEBPACK FOOTER //
// ./apps/portcall/addportcall/vessel.ts